import { DNBLogoIcon } from '@affine/component';
import { Avatar } from '@affine/component/ui/avatar';
import {
  AuthService,
  ServerConfigService,
  UserFeatureService,
} from '@affine/core/modules/cloud';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { PropertyIcon } from '@docnboard/icons/blocksuite/rc';
import { BookOpenCheckIcon, SettingsIcon } from '@docnboard/icons/rc';
import { useLiveData, useService, useServices } from '@toeverything/infra';
import clsx from 'clsx';
import { useSetAtom } from 'jotai/react';
import {
  type MouseEvent,
  type ReactElement,
  Suspense,
  type SVGProps,
  useCallback,
  useEffect,
} from 'react';

import { authAtom } from '../../../atoms/auth';
import { UserPlanButton } from '../../auth/user-plan-button';
import { useGeneralSettingList } from '../general-setting';
import type { ActiveTab, WorkspaceSubTab } from '../types';
import * as style from './style.css';

export type UserInfoProps = {
  onAccountSettingClick: () => void;
  active?: boolean;
};

export const UserInfo = ({ onAccountSettingClick, active }: UserInfoProps) => {
  const account = useLiveData(useService(AuthService).session.account$);
  if (!account) {
    // TODO(@eyhn): loading ui
    return;
  }
  return (
    <div
      data-testid="user-info-card"
      className={clsx(style.accountButton, {
        active: active,
      })}
      onClick={onAccountSettingClick}
    >
      <Avatar
        size={28}
        rounded={2}
        name={account.label}
        url={account.avatar}
        className="avatar"
      />

      <div className="content">
        <div className="name-container">
          <div className="name" title={account.label}>
            {account.label}
          </div>
          <UserPlanButton />
        </div>

        <div className="email" title={account.email}>
          {account.email}
        </div>
      </div>
    </div>
  );
};

export const SignInButton = () => {
  const t = useI18n();
  const setAuthModal = useSetAtom(authAtom);

  return (
    <div
      className={style.accountButton}
      onClick={useCallback(() => {
        setAuthModal({ openModal: true, state: 'signIn' });
      }, [setAuthModal])}
    >
      <div className="avatar not-sign">
        {/* DNB_FIX заменить иконку */}
        <DNBLogoIcon />
      </div>

      <div className="content">
        <div className="name" title={t['com.affine.settings.sign']()}>
          {t['com.affine.settings.sign']()}
        </div>
        <div className="email" title={t['com.affine.setting.sign.message']()}>
          {t['com.affine.setting.sign.message']()}
        </div>
      </div>
    </div>
  );
};

export const useWorkspaceSettingList = (): {
  key: WorkspaceSubTab;
  title: string;
  icon: (props: SVGProps<SVGSVGElement>) => ReactElement;
  testId: string;
}[] => {
  const t = useI18n();

  const { userFeatureService, serverConfigService } = useServices({
    UserFeatureService,
    ServerConfigService,
  });

  useEffect(() => {
    userFeatureService.userFeature.revalidate();
  }, [userFeatureService]);

  const hasWiki = useLiveData(
    serverConfigService.serverConfig.features$.map(r => r?.wiki)
  );

  const settings: {
    key: WorkspaceSubTab;
    title: string;
    icon: (props: SVGProps<SVGSVGElement>) => ReactElement;
    testId: string;
  }[] = [
    {
      key: 'workspace:preference',
      title: t['com.affine.settings.workspace.preferences'](),
      icon: SettingsIcon,
      testId: 'appearance-panel-trigger',
    },
    {
      key: 'workspace:properties',
      title: t['com.affine.settings.workspace.properties'](),
      icon: PropertyIcon,
      testId: 'editor-panel-trigger',
    },
  ];

  if (hasWiki) {
    settings.push({
      key: 'workspace:wiki',
      title: t['com.affine.settings.workspace.wiki'](),
      icon: props => <BookOpenCheckIcon {...props} />,
      testId: 'shortcuts-panel-trigger',
    });
  }

  return settings;
};

export const SettingSidebar = ({
  activeTab,
  onTabChange,
}: {
  activeTab: ActiveTab;
  onTabChange: (key: ActiveTab) => void;
}) => {
  const t = useI18n();
  const loginStatus = useLiveData(useService(AuthService).session.status$);
  const generalList = useGeneralSettingList();
  const workspaceList = useWorkspaceSettingList();

  const gotoTab = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const tab = e.currentTarget.dataset.eventArg;
      if (!tab) return;
      track.$.settingsPanel.menu.openSettings({ to: tab });
      onTabChange(tab as ActiveTab);
    },
    [onTabChange]
  );
  const onAccountSettingClick = useCallback(() => {
    track.$.settingsPanel.menu.openSettings({ to: 'account' });
    onTabChange('account');
  }, [onTabChange]);

  return (
    <div className={style.settingSlideBar} data-testid="settings-sidebar">
      <div className={style.sidebarTitle}>
        {t['com.affine.settingSidebar.title']()}
      </div>
      <div className={style.sidebarSubtitle}>
        {t['com.affine.settingSidebar.settings.general']()}
      </div>
      <div className={style.sidebarItemsWrapper}>
        {generalList.map(({ title, icon, key, testId }) => {
          return (
            <div
              className={clsx(style.sidebarSelectItem, {
                active: key === activeTab,
              })}
              key={key}
              title={title}
              data-event-arg={key}
              onClick={gotoTab}
              data-testid={testId}
            >
              {icon({ className: 'icon' })}
              <span className="setting-name">{title}</span>
            </div>
          );
        })}
      </div>

      <div className={style.sidebarSubtitle}>
        {t['com.affine.settingSidebar.settings.workspace']()}
      </div>
      <div className={style.sidebarItemsWrapper}>
        {workspaceList.map(({ title, icon, key, testId }) => {
          return (
            <div
              className={clsx(style.sidebarSelectItem, {
                active: key === activeTab,
              })}
              key={key}
              title={title}
              data-event-arg={key}
              onClick={gotoTab}
              data-testid={testId}
            >
              {icon({ className: 'icon' })}
              <span className="setting-name">{title}</span>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 'auto' }}>
        {loginStatus === 'unauthenticated' ? <SignInButton /> : null}
        {loginStatus === 'authenticated' ? (
          <Suspense>
            <UserInfo
              onAccountSettingClick={onAccountSettingClick}
              active={activeTab === 'account'}
            />
          </Suspense>
        ) : null}
      </div>
    </div>
  );
};
